import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import { queryClient } from "@/lib/queryClient";
import { queryKeys } from "@/lib/query-keys";

export function useNotificationsWebSocket() {
  const { user } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const invalidateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) return;

    let unmounted = false;

    async function connect() {
      if (unmounted) return;

      try {
        const tokenRes = await fetch("/api/ws-token", { credentials: "include" });
        if (!tokenRes.ok) return;
        const { token } = await tokenRes.json();

        if (unmounted) return;

        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        // Refuse to transmit the auth token over plaintext ws:// in production —
        // a misconfigured proxy or non-HTTPS deployment would otherwise leak
        // the Clerk-issued WS token in cleartext.
        if (protocol !== "wss:" && import.meta.env.PROD) {
          console.error("[ws] Refusing to send auth token over insecure ws://");
          return;
        }
        const wsUrl = `${protocol}//${window.location.host}/ws`;
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        ws.onopen = () => {
          ws.send(JSON.stringify({ type: "auth", token }));
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === "notification") {
              if (invalidateDebounceRef.current) {
                clearTimeout(invalidateDebounceRef.current);
              }
              invalidateDebounceRef.current = setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unreadCount });
                queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
              }, 500);
            }
          } catch (e) {
            // ignore
          }
        };

        ws.onclose = () => {
          if (!unmounted) {
            reconnectTimeoutRef.current = setTimeout(connect, 5000);
          }
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch (e) {
        if (!unmounted) {
          reconnectTimeoutRef.current = setTimeout(connect, 5000);
        }
      }
    }

    connect();

    return () => {
      unmounted = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (invalidateDebounceRef.current) {
        clearTimeout(invalidateDebounceRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [user]);
}
